// src/pages/rag/RagClientScriptsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ragListCollections } from "../api/client";
import "../styles/rag.css";
import "../styles/base.css";

export default function RagClientScriptsPage() {
  const { id } = useParams();
  const [kb, setKb] = useState(null);
  const [loading, setLoading] = useState(true);

  const [lang, setLang] = useState("python"); // python | node | curl
  const [mode, setMode] = useState("agentic"); // agentic | basic
  const [searchType, setSearchType] = useState("semantic"); // semantic | keyword | hybrid
  const [topK, setTopK] = useState(5);

  useEffect(() => {
    (async () => {
      try {
        const list = await ragListCollections();
        const found = list.find((c) => String(c.id) === String(id));
        setKb(found || null);
      } catch (err) {
        console.error("Failed to load KB for scripts", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const scripts = useMemo(() => {
    if (!kb) {
      return { python: "", node: "", curl: "" };
    }

    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://your-domain.com";
    const apiBase = `${origin}/api`;

    const collectionId = kb.id;
    const projectId = kb.project_id ?? 1;

    // Try to read llama / embed model from indexing_options; fallback defaults
    const llmModel =
      (kb.indexing_options && kb.indexing_options.llm_model) ||
      "llama3.1:8b";
    const embedModel = kb.embedding_model || "nomic-embed-text";

    const pythonScript = `\"\"\"Example: Call RAG + Llama for this KB

- Uses Agentic RAG or Basic RAG (mode)
- Uses semantic / keyword / hybrid search over FAISS
- LLM model (Llama) and Embedding model are explicitly documented below
\"\"\"

import requests

API_BASE = "${apiBase}"
COLLECTION_ID = ${collectionId}
PROJECT_ID = ${projectId}

# 🔹 LLM / Agentic RAG details
LLAMA_MODEL = "${llmModel}"      # Answer generation model on your Llama server
MODE = "${mode}"                 # "agentic" (Agentic RAG) or "basic"

# 🔹 Retrieval details
EMBED_MODEL = "${embedModel}"    # Embedding model used for semantic vector search
SEARCH_TYPE = "${searchType}"    # "semantic", "keyword", or "hybrid"
TOP_K = ${topK}                  # How many chunks to retrieve

ACCESS_TOKEN = "YOUR_ACCESS_TOKEN_HERE"  # e.g., JWT from SOW Analyzer

def ask_kb(query: str):
    url = f"{API_BASE}/search/collections/{COLLECTION_ID}/ask"
    params = {"project_id": PROJECT_ID}
    payload = {
        "query": query,
        "search_type": SEARCH_TYPE,
        "top_k": TOP_K,
        "mode": MODE,
    }
    headers = {
        "Content-Type": "application/json",
        "Authorization": f"Bearer {ACCESS_TOKEN}",
    }

    resp = requests.post(url, params=params, json=payload, headers=headers, timeout=120)
    resp.raise_for_status()
    data = resp.json()

    print("Answer (LLM / Llama):")
    print(data.get("answer"))
    print("\\nSource chunks (from FAISS / embeddings):")
    for i, c in enumerate(data.get("chunks", []), start=1):
        meta = c.get("metadata") or {}
        score = c.get("score", 0.0)
        print(f"[{i}] score={score:.4f} file={meta.get('filename')} chunk={meta.get('chunk_idx')}")
        print(c.get("text"))
        print("-" * 80)
    return data

if __name__ == "__main__":
    ask_kb("Summarize the key assumptions and payment milestones in this SOW")
`;

    const nodeScript = `// Example: Call RAG + Llama for this KB using Node.js (Axios)
// - Agentic RAG or Basic RAG via "mode"
// - Semantic / keyword / hybrid retrieval via "search_type"

const axios = require("axios");

const API_BASE = "${apiBase}";
const COLLECTION_ID = ${collectionId};
const PROJECT_ID = ${projectId};

// 🔹 LLM / Agentic RAG details
const LLAMA_MODEL = "${llmModel}";   // Defined on your Llama server
const MODE = "${mode}";              // "agentic" (Agentic RAG) or "basic"

// 🔹 Retrieval details
const EMBED_MODEL = "${embedModel}"; // Embedding model for semantic search
const SEARCH_TYPE = "${searchType}"; // "semantic", "keyword", "hybrid"
const TOP_K = ${topK};               // Number of chunks to retrieve

const ACCESS_TOKEN = "YOUR_ACCESS_TOKEN_HERE"; // Typically a JWT from SOW Analyzer

async function askKb(query) {
  const url = \`\${API_BASE}/search/collections/\${COLLECTION_ID}/ask\`;
  const params = { project_id: PROJECT_ID };

  const payload = {
    query,
    search_type: SEARCH_TYPE,
    top_k: TOP_K,
    mode: MODE,
  };

  const res = await axios.post(url, payload, {
    params,
    headers: {
      "Content-Type": "application/json",
      Authorization: \`Bearer \${ACCESS_TOKEN}\`,
    },
    timeout: 120000,
  });

  const data = res.data;
  console.log("Answer (LLM / Llama):");
  console.log(data.answer);
  console.log("\\nSource chunks (from FAISS / embeddings):");
  (data.chunks || []).forEach((c, idx) => {
    const meta = c.metadata || {};
    console.log(
      \`[\${idx + 1}] score=\${c.score.toFixed(4)} file=\${meta.filename} chunk=\${meta.chunk_idx}\`
    );
    console.log(c.text);
    console.log("----------------------------------------------------------------");
  });
  return data;
}

// Example: integrate into chatbot handler
(async () => {
  await askKb("What are the scope exclusions in this SOW?");
})();
`;

    const curlScript = `# Example: Call RAG + Llama for this KB using curl
# - MODE controls Agentic vs Basic RAG
# - SEARCH_TYPE controls semantic vs keyword vs hybrid

curl -X POST "${apiBase}/search/collections/${collectionId}/ask?project_id=${projectId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN_HERE" \\
  -d '{
    "query": "Summarize the key assumptions in this SOW",
    "search_type": "${searchType}",
    "top_k": ${topK},
    "mode": "${mode}"
  }'
# LLM model on server: ${llmModel}
# Embedding model used for semantic search: ${embedModel}
`;

    return {
      python: pythonScript,
      node: nodeScript,
      curl: curlScript,
    };
  }, [kb, mode, searchType, topK]);

  if (loading) {
    return <div>Loading KB for script generation...</div>;
  }

  if (!kb) {
    return <div>KB not found.</div>;
  }

  const currentScript =
    lang === "curl"
      ? scripts.curl
      : lang === "node"
      ? scripts.node
      : scripts.python;

  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://your-domain.com";
  const apiBase = `${origin}/api`;
  const ragUrl = `${apiBase}/search/collections/${kb.id}/ask?project_id=${
    kb.project_id ?? 1
  }`;

  return (
    <div>
      <h1>Client Script Generator – {kb.name}</h1>
      <p>{kb.description}</p>

      <section className="rag-section">
        <h2>RAG Endpoint Details</h2>
        <table className="rag-table">
          <tbody>
            <tr>
              <th>Collection ID</th>
              <td>{kb.id}</td>
            </tr>
            <tr>
              <th>Project / SOW</th>
              <td>{kb.project_id ?? "-"}</td>
            </tr>
            <tr>
              <th>Slug</th>
              <td>{kb.slug}</td>
            </tr>
            <tr>
              <th>Llama model (LLM)</th>
              <td>
                {(kb.indexing_options && kb.indexing_options.llm_model) ||
                  "llama3.1:8b (default – adjust in backend config)"}
              </td>
            </tr>
            <tr>
              <th>Embedding model</th>
              <td>{kb.embedding_model || "nomic-embed-text (default)"}</td>
            </tr>
            <tr>
              <th>RAG Ask URL</th>
              <td>
                <code>{ragUrl}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="rag-section" style={{ marginTop: "1.5rem" }}>
        <h2>Script Options</h2>
        <div className="rag-grid">
          <div>
            <label>Language</label>
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="python">Python</option>
              <option value="node">Node.js (Axios)</option>
              <option value="curl">cURL</option>
            </select>
          </div>

          <div>
            <label>RAG Mode</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="basic">
                Basic RAG – standard retrieval + answer
              </option>
              <option value="agentic">
                Agentic RAG – smarter query rewriting & planning
              </option>
            </select>
            <small>
              <strong>Basic RAG:</strong> Standard RAG pipeline (good for
              predictable, fast queries).
              <br />
              <strong>Agentic RAG:</strong> Planned, smarter retrieval that can
              evolve to do query rewriting, multi-step reasoning, and more
              advanced flows.
            </small>
          </div>

          <div>
            <label>Search Type</label>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="semantic">Semantic (embeddings / vector search)</option>
              <option value="keyword">Keyword (BM25 / lexical)</option>
              <option value="hybrid">Hybrid (semantic + keyword)</option>
            </select>
          </div>

          <div>
            <label>Top K chunks</label>
            <input
              type="number"
              min={1}
              max={50}
              value={topK}
              onChange={(e) => setTopK(Number(e.target.value) || 1)}
            />
          </div>
        </div>
      </section>

      <section className="rag-section" style={{ marginTop: "1.5rem" }}>
        <h2>Generated Script</h2>
        <p className="rag-help">
          Copy–paste this into your{" "}
          {lang === "python"
            ? "Python service / chatbot"
            : lang === "node"
            ? "Node.js service"
            : "shell / CI job"}{" "}
          to call <strong>RAG + Llama</strong> for this specific KB, with the
          configured mode and search type. The script includes explicit{" "}
          <strong>LLM (Llama)</strong> and <strong>Embedding</strong> model
          details.
        </p>

        <pre className="rag-code-block">
          <code>{currentScript}</code>
        </pre>
      </section>
    </div>
  );
}
